import React, { useEffect, useState } from 'react';
import { Modal, Row, Col, Form, Button, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTenant } from '../../settings/TenantContext';
import { supabase } from '../../lib/supabaseClient';

export default function QuickRepliesModal({ show, onHide, onSave, tenantId: propTenantId, siteId: propSiteId }) {
  const ctx = useTenant();
  const tenantId = propTenantId || ctx.tenantId;
  const siteId = propSiteId || ctx.siteId;
  const base = process.env.REACT_APP_SKINNY_SUPABASE_URL;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [enabled, setEnabled] = useState(false);
  const [items, setItems] = useState([
    { id: 'qr1', label: '', message_text: '' },
    { id: 'qr2', label: '', message_text: '' },
  ]);

  const InfoIcon = ({ text }) => (
    <OverlayTrigger placement="right" delay={{ show: 150, hide: 0 }} overlay={<Tooltip id="qr-tip" className="bg-dark">{text}</Tooltip>}>
      <button type="button" className="btn btn-outline-info btn-sm ms-1 p-0 d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 18, height: 18, lineHeight: '16px' }} aria-label="Help">
        <i className="bi bi-info-circle" style={{ fontSize: 12 }} aria-hidden="true"></i>
      </button>
    </OverlayTrigger>
  );

  useEffect(() => {
    (async () => {
      if (!show || !tenantId || !siteId) return;
      setLoading(true);
      setError('');
      try {
        const url = `${(base || '').replace(/\/+$/, '')}/functions/v1/autolaunch-get?tenant_id=${encodeURIComponent(tenantId)}&site_id=${encodeURIComponent(siteId)}`;
        const res = await fetch(url, { method: 'GET' });
        if (!res.ok) {
          if (res.status === 404) {
            setEnabled(false);
            setItems([
              { id: 'qr1', label: '', message_text: '' },
              { id: 'qr2', label: '', message_text: '' },
            ]);
            setLoading(false);
            return;
          }
          const txt = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status}${txt ? ` — ${txt.slice(0, 160)}` : ''}`);
        }
        const data = await res.json();
        const qr = data.quick_replies || {};
        setEnabled(!!qr.enabled);
        const loaded = Array.isArray(qr.items) ? qr.items : [];
        if (loaded.length > 0) {
          setItems(loaded.map((it, i) => ({
            id: it.id || `qr${i + 1}`,
            label: String(it.label || ''),
            message_text: String(it.message_text || ''),
          })));
        } else {
          setItems([{ id: 'qr1', label: '', message_text: '' }]);
        }
      } catch (e) {
        setError(e?.message || String(e));
      }
      setLoading(false);
    })();
  }, [show, tenantId, siteId, base]);

  function updateItem(index, field, value) {
    setItems((arr) => {
      const a = (arr || []).slice();
      if (!a[index]) a[index] = { id: `qr${index + 1}`, label: '', message_text: '' };
      a[index] = { ...a[index], [field]: value };
      return a;
    });
  }

  function addItem() {
    setItems((arr) => {
      const a = (arr || []).slice();
      if (a.length >= 6) return a;
      a.push({ id: `qr${a.length + 1}`, label: '', message_text: '' });
      return a;
    });
  }

  function removeItem(index) {
    setItems((arr) => {
      const a = (arr || []).filter((_, i) => i !== index);
      return a.length < 1
        ? [{ id: 'qr1', label: '', message_text: '' }]
        : a;
    });
  }

  function moveItem(index, direction) {
    setItems((arr) => {
      const a = (arr || []).slice();
      const target = index + direction;
      if (target < 0 || target >= a.length) return a;
      [a[index], a[target]] = [a[target], a[index]];
      return a;
    });
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const validItems = (items || []).filter(it => (it.label || '').trim() && (it.message_text || '').trim());
      if (enabled && (validItems.length < 1 || validItems.length > 6)) {
        throw new Error(`Quick replies require 1–6 items when enabled (got ${validItems.length} with both label and message filled in).`);
      }
      const body = {
        tenant_id: tenantId,
        site_id: siteId,
        enabled: !!enabled,
        items: validItems.map(it => ({
          id: it.id || '',
          label: String(it.label || '').trim().slice(0, 80),
          message_text: String(it.message_text || '').trim().slice(0, 500),
        })),
      };
      const tok = (await supabase.auth.getSession()).data.session?.access_token || '';
      const headers = tok ? { Authorization: `Bearer ${tok}` } : undefined;
      const { error } = await supabase.functions.invoke('quick-replies-upsert', { body, headers });
      if (error) throw error;
      onSave && onSave(body);
      onHide && onHide(true);
    } catch (e) {
      const name = (e && e.name) ? String(e.name) : '';
      const msg = (e && e.message) ? String(e.message) : String(e);
      setError(name ? `${name}: ${msg}` : msg);
    }
    setSaving(false);
  }

  const validCount = (items || []).filter(it => (it.label || '').trim() && (it.message_text || '').trim()).length;

  return (
    <Modal show={!!show} onHide={() => onHide && onHide(false)} centered size="lg" enforceFocus={false}>
      <Modal.Header closeButton><Modal.Title>Quick Replies</Modal.Title></Modal.Header>
      <Modal.Body>
        {loading ? <div className="text-muted">Loading…</div> : (
          <>
            {error ? <Alert variant="danger">{error}</Alert> : null}
            <Form.Check
              type="switch"
              id="qr-enabled"
              label={<>Enable Quick Replies <InfoIcon text="When enabled, clickable suggestion buttons appear in the chat widget to guide visitors toward common actions. Buttons show after the greeting or when the chat is opened with no conversation history." /></>}
              checked={enabled}
              onChange={(e) => setEnabled(!!(e && e.target && e.target.checked))}
              className="mb-3"
            />
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-semibold">
                Buttons ({validCount})
                <InfoIcon text="Add 2–6 quick-reply buttons. Each has a visible label and a message that gets sent when clicked. Visitors see the label; clicking sends the message text to the AI." />
              </div>
              <Button variant="outline-secondary" size="sm" onClick={addItem} disabled={items.length >= 6}>Add Button</Button>
            </div>
            {items.map((it, i) => (
              <Row key={it.id || i} className="g-2 mb-2 align-items-start">
                <Col md={4}>
                  <Form.Control
                    placeholder={`Button label ${i + 1}`}
                    value={it.label}
                    onChange={(e) => updateItem(i, 'label', e.target.value)}
                    maxLength={80}
                  />
                  <Form.Text muted className="d-block" style={{ fontSize: 11 }}>{(it.label || '').length}/80</Form.Text>
                </Col>
                <Col md={5}>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    placeholder="Message sent when clicked…"
                    value={it.message_text}
                    onChange={(e) => updateItem(i, 'message_text', e.target.value)}
                    maxLength={500}
                  />
                  <Form.Text muted className="d-block" style={{ fontSize: 11 }}>{(it.message_text || '').length}/500</Form.Text>
                </Col>
                <Col md={3} className="d-flex gap-1 align-items-center">
                  <Button variant="outline-secondary" size="sm" onClick={() => moveItem(i, -1)} disabled={i === 0} title="Move up">&uarr;</Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} title="Move down">&darr;</Button>
                  <Button variant="outline-danger" size="sm" onClick={() => removeItem(i)} disabled={items.length <= 1}>Remove</Button>
                </Col>
              </Row>
            ))}
            {enabled && validCount < 1 && (
              <Alert variant="warning" className="mt-2 py-1 px-2" style={{ fontSize: 13 }}>
                At least 1 button with both label and message is required when enabled.
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end">
        <Button variant="outline-secondary" onClick={() => onHide && onHide(false)}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || loading || !tenantId || !siteId}>{saving ? 'Saving…' : 'Save'}</Button>
      </Modal.Footer>
    </Modal>
  );
}
